import { createApiClient } from 'utils/AxiosClient';

const API_BASE_URL = `${process.env.REACT_APP_TK_GW_DOMAIN}/v1/timekeeper`;

// ──────────────────────────────────────────────────────────────────────────────
// Tipos
// ──────────────────────────────────────────────────────────────────────────────

export interface PushSubscribeResponse {
    id: string;
    user_key: string;
    endpoint: string;
    activo: boolean;
}

export interface PushNotificationPayload {
    title: string;
    body: string;
    icon?: string;
    badge?: string;
    url?: string;
    tag?: string;
    renotify?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────────
// Utilidades VAPID
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Convierte una clave pública VAPID en base64url a Uint8Array,
 * tal como requiere PushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

// ──────────────────────────────────────────────────────────────────────────────
// Servicio principal
// ──────────────────────────────────────────────────────────────────────────────

export class PushNotificationService {
    private readonly apiClient = createApiClient(`${API_BASE_URL}/push`);

    // ── Registro del Service Worker ──────────────────────────────────────────

    /**
     * Registra el service worker `sw.js` servido desde la raíz del sitio.
     * Retorna el ServiceWorkerRegistration o null si el navegador no soporta SW.
     */
    async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
        if (!('serviceWorker' in navigator)) {
            console.warn('[PushNotificationService] Service Worker no soportado');
            return null;
        }

        try {
            const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
            console.info('[PushNotificationService] SW registrado:', registration.scope);
            return registration;
        } catch (error) {
            console.error('[PushNotificationService] Error al registrar SW:', error);
            return null;
        }
    }

    // ── Permisos ─────────────────────────────────────────────────────────────

    /**
     * Solicita permiso de notificaciones al usuario.
     * Retorna true si fue concedido.
     */
    async requestPermission(): Promise<boolean> {
        if (!('Notification' in window)) {
            console.warn('[PushNotificationService] Notifications API no soportada');
            return false;
        }

        const permission = await Notification.requestPermission();
        return permission === 'granted';
    }

    // ── Suscripción ───────────────────────────────────────────────────────────

    /**
     * Obtiene la clave pública VAPID del backend y suscribe el navegador.
     * Luego envía la suscripción al backend para persistirla.
     */
    async subscribe(): Promise<PushSubscribeResponse | null> {
        const registration = await this.registerServiceWorker();
        if (!registration) return null;

        const granted = await this.requestPermission();
        if (!granted) {
            console.warn('[PushNotificationService] Permiso denegado');
            return null;
        }

        // Obtener clave VAPID pública del backend
        const vapidKey = await this.fetchVapidPublicKey();
        if (!vapidKey) return null;

        try {
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey),
            });

            return await this.sendSubscriptionToBackend(subscription);
        } catch (error) {
            console.error('[PushNotificationService] Error al suscribirse:', error);
            return null;
        }
    }

    /**
     * Retorna la suscripción activa del navegador, o null si no hay ninguna.
     */
    async getCurrentSubscription(): Promise<PushSubscription | null> {
        if (!('serviceWorker' in navigator)) return null;

        const registration = await navigator.serviceWorker.getRegistration('/sw.js');
        if (!registration) return null;

        return registration.pushManager.getSubscription();
    }

    // ── Métodos privados ──────────────────────────────────────────────────────

    private async fetchVapidPublicKey(): Promise<string | null> {
        try {
            const { data } = await this.apiClient.get<{ public_key: string }>('/vapid-public-key');
            return data.public_key;
        } catch (error) {
            console.error('[PushNotificationService] Error al obtener VAPID key:', error);
            return null;
        }
    }

    private async sendSubscriptionToBackend(
        subscription: PushSubscription,
    ): Promise<PushSubscribeResponse | null> {
        const json = subscription.toJSON();
        const keys = json.keys as { p256dh: string; auth: string };

        try {
            const { data } = await this.apiClient.post<PushSubscribeResponse>('/subscribe', {
                endpoint: subscription.endpoint,
                p256dh: keys.p256dh,
                auth: keys.auth,
            });
            return data;
        } catch (error) {
            console.error('[PushNotificationService] Error al enviar suscripción al backend:', error);
            return null;
        }
    }
}

export const pushNotificationService = new PushNotificationService();
